/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { signOut } from "aws-amplify/auth";
import { RootState, useAppDispatch } from "@store/store";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useLazyGetCuentasByIdQuery } from "@api/account";
import { ApiError } from "@interfaces/response";
import IconSpeed from "@components/Images/IconSpeed";
import { useLazyGetLoanApplicationByIdQuery, usePostCloneLoanApplicationMutation } from "@api/applications";
import IconRejected from "@components/Images/IconRejected";

const RECHAZO_VENCIDO = "CSREO";

const RechazoVencido = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const personalData = useSelector((state: RootState) => state.register.personal_data);
  const applicationData = useSelector((state: RootState) => state.register.application);

  const [getAccountData] = useLazyGetCuentasByIdQuery();
  const [getLoanAplication] = useLazyGetLoanApplicationByIdQuery();
  const [postCloneLoanApplication] = usePostCloneLoanApplicationMutation();

  const [name, setName] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);
  const [open, setOpen] = useState(false);

  const closeModal = () => {
    setOpen(false);
  };

  const handleClick = async () => {
    await signOut();
    navigate("/login");
  };

  const fetchData = async () => {
    try {
      const response = await getAccountData({ id: personalData.id }).unwrap();
      const account_data = response.data;
      const fullName = `${account_data.name} ${account_data.last_name} ${account_data.last_name_2}`;
      setName(fullName);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    }
  };

  const loandApplication = async () => {
    try {
      const application = await getLoanAplication(applicationData.id).unwrap();
      if (application?.status_catalog?.code !== RECHAZO_VENCIDO) {
        setIsDisabled(true);
        await handleClick();
      }
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    }
  };

  const handleCloneApplication = async () => {
    try {
      setIsDisabled(true);
      setOpen(true);
      await postCloneLoanApplication({ solicitud_id: applicationData.id }).unwrap();
      await handleClick();
    } catch (error) {
      setOpen(false);
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setOpen(false);
      setIsDisabled(false);
    }
  };

  useEffect(() => {
    document.body.style.backgroundColor = theme?.palette?.primary?.dark || "";
    fetchData();
    loandApplication();
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8} mt={5}>
          <InactivityHandler />
          <Box>
            <Typography
              variant={isMobileOrTablet ? "h4" : "h3"}
              color={theme?.palette?.primary?.light}
              fontWeight={500}
              align="center"
            >
              ¡Hola!
            </Typography>

            <Typography
              variant={isMobileOrTablet ? "h4" : "h3"}
              color={theme?.palette?.primary?.light}
              fontWeight={500}
              align="center"
            >
              {name}
            </Typography>

            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.otherColors?.main}
              align="center"
              mt={3}
            >
              Lamentamos que no pudieramos haber autorizado tu solicitud anteriormente.
            </Typography>

            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.otherColors?.main}
              align="center"
              mt={3}
              fontWeight={700}
            >
              ¡Puedes volver a intentarlo!
            </Typography>

            <Box display="flex" justifyContent="center" mt={4}>
              <IconSpeed width={isMobileOrTablet ? "110" : "170"} fill={theme.palette.primary.light} />
            </Box>

            <Box display="flex" justifyContent="center" mt={3}>
              <Button variant="default" onClick={handleCloneApplication} disabled={isDisabled}>
                Volver a intentar
              </Button>
            </Box>

            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.otherColors?.main}
              align="center"
              mt={3}
            >
              Para cualquier duda o aclaración contáctanos a: <br />
              Tel:{" "}
              <Link href="tel:+525591044964" color={theme?.palette?.otherColors?.main}>
                55 9104 4964
              </Link>
            </Typography>

            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.otherColors?.main}
              align="center"
              fontWeight={300}
              mt={1}
            >
              Correo:{" "}
              <Link href="mailto:contacto@lendia.mx" color={theme?.palette?.otherColors?.main}>
                contacto@lendia.mx
              </Link>
            </Typography>
          </Box>
        </Grid>

        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason !== "backdropClick") {
            closeModal();
          }
        }}
      >
        <DialogTitle sx={{ textAlign: "center", color: theme?.palette?.primary?.dark }}>
          Nueva solicitud registrada
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" mt={2} mb={2}>
            <IconRejected width={isMobileOrTablet ? "110" : "170"} fill={theme.palette.primary.light} />
          </Box>
          <Typography variant="body1" color={theme?.palette?.primary?.main} align="center" mb={3}>
            Hemos registrado tu nueva solicitud. <br />
            Se cerrara la sesión para que puedas iniciar con tus mismos datos en la nueva solicitud.
            <br />
            Esto puede tardar unos segundos.
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RechazoVencido;
